import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const RyadProject = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const openGoogleMaps = () => {
    window.open('https://maps.app.goo.gl/5yFLoZp2zbUJbJJo7', '_blank');
  };

  const features = [
    { icon: '🏢', key: 'r5', gradient: 'from-blue-500 to-blue-600' },
    { icon: '🍳', key: 'cuisine', gradient: 'from-red-500 to-red-600' },
    { icon: '💪', key: 'gym', gradient: 'from-amber-500 to-amber-600' },
    { icon: '🌇', key: 'penthouse', gradient: 'from-purple-500 to-purple-600' },
  ];

  return (
    <section id="projet" ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className={`text-center mb-20 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-6 tracking-wide uppercase">
            Découvrez notre projet
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span className="gradient-text">{t.project.title}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.project.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          {/* Left: Images Grid - Enhanced */}
          <div className={`relative transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="group relative overflow-hidden rounded-2xl shadow-xl card-hover">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_luxury-ryad/artifacts/8m0mf6b4_SALON%201.jpg"
                    alt="Modern living room"
                    className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 text-white">
                      <span className="text-sm font-semibold">Salon Moderne</span>
                    </div>
                  </div>
                </div>
                <div className="group relative overflow-hidden rounded-2xl shadow-xl card-hover">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_luxury-ryad/artifacts/tr0hclw7_CUISINE.png"
                    alt="Modern kitchen"
                    className="w-full h-48 object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 text-white">
                      <span className="text-sm font-semibold">Cuisine Équipée</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-8">
                <div className="group relative overflow-hidden rounded-2xl shadow-xl card-hover h-[calc(100%-32px)]">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_luxury-ryad/artifacts/al99fn19_CHAMBRE%20PARENTS%203.jpg"
                    alt="Master bedroom"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 text-white">
                      <span className="text-sm font-semibold">Suite Parentale</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative element */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-amber-500/20 rounded-full blur-2xl"></div>
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl"></div>
          </div>

          {/* Right: Key Features - Modernized */}
          <div className={`space-y-8 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
            {/* Main Features Grid */}
            <div className="grid grid-cols-2 gap-5">
              {features.map((feature, index) => (
                <div 
                  key={feature.key}
                  className="group relative bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 card-hover border border-gray-100 overflow-hidden"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                  <div className="relative z-10">
                    <div className="text-4xl mb-4 transform group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-300">
                      {feature.icon}
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-amber-600 transition-colors">
                      {t.project.features[feature.key]}
                    </h3>
                    <p className="text-gray-500 text-sm">
                      {t.project.features[`${feature.key}_desc`]}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Timeline - Premium Design */}
            <div className="relative bg-gradient-to-br from-amber-50 via-amber-100/50 to-orange-50 p-8 rounded-3xl overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-amber-300/30 rounded-full blur-3xl"></div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center relative z-10">
                {t.project.planning}
              </h3>
              
              <div className="relative flex items-center justify-between z-10">
                {/* Start */}
                <div className="text-center group">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-3xl mb-4 mx-auto shadow-lg shadow-blue-500/30 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    📅
                  </div>
                  <p className="font-bold text-gray-900 text-lg">{t.project.timeline.start}</p>
                  <p className="text-gray-600 text-sm">{t.project.timeline.start_desc}</p>
                </div>

                {/* Progress Line */}
                <div className="flex-1 mx-6 relative">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full w-1/3 bg-gradient-to-r from-blue-500 via-amber-400 to-amber-200 rounded-full animate-pulse"></div>
                  </div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-3 py-1 rounded-full text-xs font-semibold text-amber-600 shadow-md">
                    18 mois
                  </div>
                </div>

                {/* End */}
                <div className="text-center group">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white text-3xl mb-4 mx-auto shadow-lg shadow-green-500/30 transform group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300">
                    🗝️
                  </div>
                  <p className="font-bold text-gray-900 text-lg">{t.project.timeline.end}</p>
                  <p className="text-gray-600 text-sm">{t.project.timeline.end_desc}</p>
                </div>
              </div>
            </div>

            {/* Location Button - Premium */}
            <button
              onClick={openGoogleMaps}
              className="group w-full relative bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-5 px-8 rounded-2xl font-semibold text-lg transition-all duration-500 transform hover:scale-[1.02] hover:-translate-y-1 shadow-xl hover:shadow-amber-500/30 flex items-center justify-center overflow-hidden btn-shine"
            >
              <svg className="w-6 h-6 mr-3 group-hover:animate-bounce" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span className="relative z-10">{t.project.location_btn}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RyadProject;
